import { InvitationStageFilter, InvitationStageBadge, type InvitationStage } from "../invitation-stage-filter.tsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { showError } from "../../../../utils/toast.ts";
import { TailSpin } from "react-loader-spinner";

import { displayFormatDate } from "../../../../../utils/helpers.js";
import { Select, type SelectOption } from "../../../ui/select/select.tsx";
import { Button } from "../../../ui/button/button.tsx";
import {
  ResponsiveDataTable,
  type ResponsiveColumn,
} from "../../../ui/responsive-table/index.ts";
import { EmptyStateMessage } from "../../../shared/empty-state-message/empty-state-message.tsx";
import { Card } from "../../../shared/card/card.tsx";
import type { OnboardInvitationTableRow } from "../schemas/onboard-invitation-row.schema.ts";
import { usePreJoinInvitations } from "../hooks/use-pre-join-invitations.ts";
import { InvitePreJoinModal } from "../modals/invite-pre-join-modal.tsx";
import styles from "./pre-join-form-tab.module.css";
import { formatName } from "../../../../utils/format-name.ts";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";

export type PreJoinFormTabProps = {
  active?: boolean;
  employeeOptions: SelectOption[];
  employeeOptionsLoading?: boolean;
  selectedEmployeeId: string;
  onSelectedEmployeeIdChange: (employeeId: string) => void;
    consultantType?: "REGULAR" | "INDIAN_CONSULTANT" | "DIRECT" | "VENDOR_BASED";

};

export function PreJoinFormTab({
  active = true,
  employeeOptions,
  employeeOptionsLoading = false,
  selectedEmployeeId,
    consultantType,
  onSelectedEmployeeIdChange,
}: PreJoinFormTabProps) {
  const isSqe = useSqeLabs();
  const [stage, setStage] = useState<InvitationStage>("ALL");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [inviteOpen, setInviteOpen] = useState(false);
  const { rows, totalElements, loading, error, refetch } = usePreJoinInvitations(
    selectedEmployeeId,
    page,
    pageSize,
   consultantType,
    active,
    stage,
  );

  useEffect(() => { if (error) showError(error); }, [error]);

  useEffect(() => {
    setPage(1);
  }, [selectedEmployeeId]);

  const tableRows: OnboardInvitationTableRow[] = useMemo(
    () =>
      rows.map((r, i) => ({
        ...r,
        id: `${String(r.email ?? "email")}-${String(r.createdOn ?? "d")}-${page}-${i}`,
      })),
    [rows, page],
  );

  const columns = useMemo<ResponsiveColumn<OnboardInvitationTableRow>[]>(
    () => [
     {
  id: "name",
  header: "Employee Name",
  accessor: "name",
  sortable: true,
  truncate: true,
  render: (row) => formatName(row.name ?? ""),
},
      {
        id: "email",
        header: "Email",
        accessor: "email",
        sortable: true,
        // truncate: true,
      },
      {
        id: "createdOn",
        header: "Invitation Date",
        accessor: "createdOn",
        sortable: true,
        render: (row) => (
          <span className="body-sm">
            {displayFormatDate(row.createdOn ?? "")}
          </span>
        ),
        getSortValue: (row) => new Date(row.createdOn ?? 0).getTime(),
        getTitleText: (row) => displayFormatDate(row.createdOn ?? ""),
      },
      {
        id: "status",
        header: "Status",
        accessor: "onboardingStage",
        sortable: true,
        nowrap: true,
        render: (row) => <InvitationStageBadge stage={row.onboardingStage} />,
      },
    ],
    [],
  );

  const showPagination = selectedEmployeeId.trim() === "";

  const handlePageSizeChange = useCallback((next: number) => {
    setPageSize(next);
    setPage(1);
  }, []);

  return (
    <div className={styles.root}>
      <Card className={styles.toolbarCard}>
        <div className={styles.toolbar}>
          <div className={styles.filter}>
            <Select
              id="pre-join-employee-filter"
              label="Select Employee Name"
              placeholder={employeeOptionsLoading ? "Loading..." : "All"}
              disabled={employeeOptionsLoading}
              isClearable
              searchable
              value={selectedEmployeeId}
              onChange={(v) => onSelectedEmployeeIdChange(v)}
              options={employeeOptions}
            />
          </div>
          <div className={styles.filter}>
            {isSqe ? (
              <Select id="pre-join-stage" label="Select Status" value={stage} options={[{ value: "ALL", label: "All" }, { value: "FORM_PENDING", label: "Candidate Pending" }, { value: "COMPLETED", label: "Form Filled" }]} onChange={(value) => { setStage(value as InvitationStage); setPage(1); }} />
            ) : <InvitationStageFilter id="pre-join-stage" value={stage} onChange={(value) => { setStage(value); setPage(1); }} />}
          </div>
          <div className={styles.toolbarRight}>
            {active && <Button type="button" variant="accent" size="md" onClick={() => setInviteOpen(true)}>
              Invite Employee
            </Button>}
          </div>
        </div>
      </Card>

      {loading ? (
        <div className={styles.loaderWrap} aria-busy="true" aria-label="Loading invitations">
          <TailSpin height={56} width={56} color="var(--color-accent-500, #2563eb)" visible />
        </div>
      ) : (
        <ResponsiveDataTable<OnboardInvitationTableRow>
          serialNumber
          columns={columns}
          data={tableRows}
          defaultSortKey="createdOn"
          defaultSortDir="desc"
          aria-label="Pre-join invitations"
          emptyState={<EmptyStateMessage message="No records available." />}
          {...(showPagination
            ? {
              pagination: true as const,
              page,
              pageSize,
              totalItems: totalElements,
              onPageChange: setPage,
              onPageSizeChange: handlePageSizeChange,
              pageSizeOptions: [5, 10, 20, 30, 40] as const,
              totalLabel: `Total Invites: ${totalElements}`
            }
            : { pagination: false as const })}
        />
      )}

      <InvitePreJoinModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={() => void refetch()}
      />
    </div>
  );
}
